import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CompletionBar from '../CompletionBar';
import { getOwnerLabel, getOwnerHelper } from '../../../constants/operationLabels';
import OwnerPersonForm, { DEFAULT_OWNER_VALUE } from '../../owner/OwnerPersonForm';
import { champsManquantsProprietaire, estPersonneMorale } from '../reglesEtapes';
import ChampsManquants from '../ChampsManquants';
import BarreEtape from '../BarreEtape';

const Step4_Owner = ({ data, updateData, nextStep, prevStep }) => {
  // Initialiser le mode en fonction des données déjà saisies (évite la perte au retour)
  const initialMode = data.contact_proprietaire_id
    ? 'select'
    : (data.nouveau_contact?.nom || data.nouveau_contact?.prenom || data.nouveau_contact?.societe ? 'create' : 'select');
  const [mode, setMode] = useState(initialMode);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateContact, setDuplicateContact] = useState(null);
  // lot AB1 — verdict du serveur (seul juge du doublon) : { doublon, rattachement_possible }
  const [duplicateVerdict, setDuplicateVerdict] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  // Ref pour ne pas écraser les données au premier mount
  const firstRenderRef = React.useRef(true);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadContacts();
  }, []);

  // Restaurer le libellé du contact sélectionné quand on revient sur l'étape
  useEffect(() => {
    if (mode === 'select' && data.contact_proprietaire_id && contacts.length > 0 && !searchTerm) {
      const selected = contacts.find(c => c.id === data.contact_proprietaire_id);
      if (selected) {
        setSearchTerm(`${selected.prenom || ''} ${selected.nom || ''}`.trim());
      }
    }
  }, [contacts, data.contact_proprietaire_id, mode]);

  // Reset uniquement sur changement manuel de mode par l'utilisateur, pas au premier mount
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (mode === 'select') {
      updateData({ 
        nouveau_contact: { ...DEFAULT_OWNER_VALUE },
        forcer_nouveau_contact: false
      });
    } else if (mode === 'create') {
      updateData({ contact_proprietaire_id: null, forcer_nouveau_contact: false, rattachement_depuis_doublon: null });
    }
  }, [mode]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/contacts`);
      setContacts(response.data || []);
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Recherche en temps réel
  useEffect(() => {
    if (searchTerm.length >= 2) {
      const filtered = contacts.filter(contact => {
        const search = searchTerm.toLowerCase();
        return (
          contact.nom?.toLowerCase().includes(search) ||
          contact.prenom?.toLowerCase().includes(search) ||
          (contact.emails && contact.emails.some(e => e.email?.toLowerCase().includes(search))) ||
          (contact.telephones && contact.telephones.some(t => t.numero?.includes(search)))
        );
      });
      setSearchResults(filtered.slice(0, 5)); // Max 5 résultats
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, contacts]);

  const selectContact = (contact) => {
    console.log('✅ Contact sélectionné:', contact);
    updateData({ contact_proprietaire_id: contact.id, rattachement_depuis_doublon: null });
    setSearchTerm(`${contact.prenom || ''} ${contact.nom || ''}`.trim());
    setSearchResults([]);
  };

  // lot Z1 — règle UNIQUE partagée avec PropertyCreationWizard.canProceedToStep(5).
  // Ne recopiez pas la condition ici : c'est cette duplication qui rendait impossible la
  // création d'un bien avec un propriétaire personne morale.
  const manquants = champsManquantsProprietaire(data, mode);
  const isComplete = manquants.length === 0;

  const checkDuplicatesAndContinue = async () => {
    if (mode === 'create' && isComplete) {
      const email = data.nouveau_contact.email?.trim();
      const telephone = data.nouveau_contact.telephone?.trim();

      // lot AB1 — la détection est faite PAR LE SERVEUR, avec la règle qu'il appliquera
      // à l'enregistrement. Le filtrage côté navigateur divergeait (contacts non chargés,
      // natures ignorées) et laissait passer des rattachements silencieux.
      setCheckingDuplicate(true);
      try {
        const { data: verdict } = await axios.post(`${API}/api/contacts/verifier-doublon`, {
          email,
          telephone,
          type: data.nouveau_contact.type,
        });
        if (verdict?.doublon) {
          setDuplicateVerdict(verdict);
          setDuplicateContact(verdict.doublon);
          setShowDuplicateModal(true);
          return;
        }
      } catch (e) {
        console.error('Vérification doublon impossible:', e);
      } finally {
        setCheckingDuplicate(false);
      }
    }
    updateData({ forcer_nouveau_contact: false });
    nextStep();
  };

  const useExistingContact = () => {
    updateData({ 
      contact_proprietaire_id: duplicateContact.id,
      nouveau_contact: { ...DEFAULT_OWNER_VALUE },
      forcer_nouveau_contact: false,
      // lot AB1 — trace du chemin emprunté : le serveur doit annoncer un rattachement
      // consécutif à un doublon, et non une simple sélection de contact.
      rattachement_depuis_doublon: duplicateContact.id,
    });
    setMode('select');
    setShowDuplicateModal(false);
    setDuplicateContact(null);
    setDuplicateVerdict(null);
    nextStep();
  };

  const continueWithNewContact = () => {
    // lot AB1 — cette intention DOIT partir au serveur : sans ce drapeau, il retrouvait
    // le même contact et rattachait le bien malgré le choix fait ici.
    updateData({ forcer_nouveau_contact: true });
    setShowDuplicateModal(false);
    setDuplicateContact(null);
    setDuplicateVerdict(null);
    nextStep();
  };

  return (
    <div className="h-full flex flex-col">
      <CompletionBar data={data} currentStep={4} />

      <div className="mb-3 mt-3">
        <h2 className="text-lg font-bold text-gray-900 mb-0.5">
          {getOwnerLabel(data.operation)}
        </h2>
        <p className="text-xs text-gray-600">
          {getOwnerHelper(data.operation)} Sélectionnez un contact existant ou créez-en un nouveau. <span className="text-red-500 font-bold">*</span>
        </p>
      </div>

      {/* Choix du mode */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setMode('select')}
          className={`flex-1 px-3 py-2 text-sm font-semibold rounded transition-colors ${
            mode === 'select'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <i className="bi bi-search mr-2"></i>
          Rechercher un contact
        </button>
        <button
          onClick={() => setMode('create')}
          className={`flex-1 px-3 py-2 text-sm font-semibold rounded transition-colors ${
            mode === 'create'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <i className="bi bi-plus-circle mr-2"></i>
          Créer un nouveau contact
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {mode === 'select' ? (
          /* Mode Recherche */
          <div className="space-y-2">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher par nom, prénom, email ou téléphone..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 pl-9"
              />
              <i className="bi bi-search absolute left-3 top-2.5 text-gray-400"></i>
            </div>

            {/* Afficher le contact sélectionné */}
            {data.contact_proprietaire_id && searchTerm && searchResults.length === 0 && (
              <div className="bg-green-50 border border-green-200 rounded p-3 flex items-center gap-2">
                <i className="bi bi-check-circle-fill text-green-600"></i>
                <span className="text-sm text-green-800 font-medium">Contact sélectionné : {searchTerm}</span>
              </div>
            )}

            {/* Résultats de recherche */}
            {searchResults.length > 0 && (
              <div className="border border-gray-200 rounded bg-white divide-y max-h-64 overflow-y-auto">
                {searchResults.map((contact) => {
                  const displayName = `${contact.titre || ''} ${contact.prenom || ''} ${contact.nom || ''}`.trim() || 'Contact sans nom';
                  const initials = `${contact.prenom?.[0] || ''}${contact.nom?.[0] || ''}`.toUpperCase() || '?';
                  return (
                    <button
                      key={contact.id}
                      onClick={() => selectContact(contact)}
                      className="w-full p-3 text-left hover:bg-blue-50 transition-colors flex items-center gap-3"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xs">
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">
                          {displayName}
                        </div>
                        <div className="text-xs text-gray-600 truncate">
                          {contact.telephones?.[0]?.numero || contact.emails?.[0]?.email || 'Pas de coordonnées'}
                        </div>
                      </div>
                      <i className="bi bi-chevron-right text-gray-400"></i>
                    </button>
                  );
                })}
              </div>
            )}

            {searchTerm.length >= 2 && searchResults.length === 0 && (
              <div className="text-center py-6 text-gray-500 text-sm">
                <i className="bi bi-inbox text-3xl mb-2 block"></i>
                Aucun contact trouvé
              </div>
            )}

            {searchTerm.length < 2 && (
              <div className="text-center py-6 text-gray-400 text-sm">
                <i className="bi bi-search text-3xl mb-2 block"></i>
                Tapez au moins 2 caractères pour rechercher
              </div>
            )}
          </div>
        ) : (
          /* Mode Création — bloc partagé avec la fiche d'édition (OwnerPersonForm) */
          <div>
            <OwnerPersonForm
              value={data.nouveau_contact}
              onChange={(next) => updateData({ nouveau_contact: next })}
              mode="wizard"
              showAddress={true}
              showNotes={false}
            />
          </div>
        )}
      </div>

      <BarreEtape
        manquants={<ChampsManquants champs={manquants} testId="wizard-step4-champs-manquants" />}
      >
        <button
          onClick={prevStep}
          className="h-11 md:h-auto px-5 md:py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded transition-colors"
        >
          Retour
        </button>
        <button
          onClick={checkDuplicatesAndContinue}
          disabled={!isComplete || checkingDuplicate}
          data-testid="wizard-step4-next"
          className="h-12 md:h-auto px-5 md:py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded transition-colors"
        >
          {checkingDuplicate ? 'Vérification…' : 'Suivant'}
        </button>
      </BarreEtape>

      {/* Modale doublon — lot AB1 : elle énonce ce que fera l'enregistrement, et les deux
          issues sont réellement transmises au serveur. */}
      {showDuplicateModal && duplicateContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4" data-testid="wizard-duplicate-modal">
            <div className="flex items-center gap-3 mb-4">
              <i className="bi bi-exclamation-triangle-fill text-orange-500 text-2xl"></i>
              <h3 className="text-lg font-bold">Coordonnée déjà utilisée</h3>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <div className="font-semibold" data-testid="wizard-duplicate-name">
                {duplicateContact.nom_affichage}
                <span className="ml-2 text-xs font-normal text-slate-600">
                  {duplicateContact.nature === 'personne_morale'
                    ? 'personne morale'
                    : duplicateContact.nature === 'agence' ? 'agence' : 'personne physique'}
                  {duplicateContact.role ? ` · ${duplicateContact.role}` : ''}
                </span>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {duplicateContact.telephone && (
                  <div><i className="bi bi-telephone mr-2"></i>{duplicateContact.telephone}</div>
                )}
                {duplicateContact.email && (
                  <div><i className="bi bi-envelope mr-2"></i>{duplicateContact.email}</div>
                )}
              </div>
            </div>

            {duplicateVerdict?.rattachement_possible === false ? (
              <div
                className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3 mb-4"
                data-testid="wizard-duplicate-nature-warning"
              >
                Cette fiche est une <b>{duplicateContact.nature === 'personne_morale' ? 'personne morale' : duplicateContact.nature === 'agence' ? 'agence' : 'personne physique'}</b>,
                alors que vous saisissez une <b>{duplicateVerdict?.nature_saisie === 'personne_morale' ? 'personne morale' : duplicateVerdict?.nature_saisie === 'agence' ? 'agence' : 'personne physique'}</b>.
                Aucun rattachement ne sera fait : une fiche distincte sera créée, ce qui est
                légitime — deux sociétés d&apos;un même gérant partagent souvent un téléphone.
              </div>
            ) : (
              <p className="text-sm text-gray-700 mb-4">
                Cette coordonnée appartient déjà à <b>{duplicateContact.nom_affichage}</b>. En
                choisissant « Rattacher », le bien sera rattaché à <b>cette fiche</b> et les
                informations saisies à l&apos;étape propriétaire (
                {estPersonneMorale(data.nouveau_contact)
                  ? 'raison sociale, forme juridique, SIREN, siège social, représentant'
                  : 'nom, prénom, coordonnées'}
                ) <b>ne seront pas enregistrées</b>.
              </p>
            )}

            <div className="flex gap-2">
              {duplicateVerdict?.rattachement_possible !== false && (
                <button
                  onClick={useExistingContact}
                  data-testid="wizard-duplicate-link-btn"
                  className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded transition-colors"
                >
                  Rattacher à cette fiche
                </button>
              )}
              <button
                onClick={continueWithNewContact}
                data-testid="wizard-duplicate-create-btn"
                className={`flex-1 px-3 py-2 text-sm font-semibold rounded transition-colors ${
                  duplicateVerdict?.rattachement_possible === false
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                Créer une fiche distincte
              </button>
              <button
                onClick={() => { setShowDuplicateModal(false); setDuplicateContact(null); setDuplicateVerdict(null); }}
                data-testid="wizard-duplicate-cancel-btn"
                className="px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded transition-colors"
              >
                Corriger la saisie
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step4_Owner;